import type { Color } from '@ccc/shared-types';
import type { RuleModifier } from '@ccc/rule-engine';
import {
  pawnRelentlessMarch,
  pawnRewindAndAdvance,
  pawnFrontalAssault,
  pawnOmnipresentPassant,
  pawnVanguardPromotion,
  pawnAscendantRoyalty,
  pawnMiniRooks,
  knightDoubleTrot,
  knightChillKnight,
  knightTacticalSwap,
  knightShacklingAnchor,
  bishopVaulting,
  bishopCardinalNudge,
  rookVaulting,
  rookLiteralFortress,
  rookRookZone,
  queenRoyalCavalier,
  queenShortRangeBurst,
  queenTheMatriarchy,
  kingTelekineticStrike,
  kingPhalanxCastle,
  applyStartingSetupForCard,
} from './piece-cards.js';
import {
  generalMomentumCheck,
  generalVaultingArmy,
  generalRetaliatoryStrike,
  generalKnightedRetinue,
  generalStoredTempo,
  generalFlexibleDeployment,
  generalRoyalEscort,
  generalCastleMaster,
} from './general-cards.js';

/**
 * Factory: cardId + side → RuleModifier.
 * Returns undefined for unknown ids.
 */
export function modifierForCard(cardId: string, side: Color): RuleModifier | undefined {
  switch (cardId) {
    case 'pawn-relentless-march': return pawnRelentlessMarch(side);
    case 'pawn-rewind-and-advance': return pawnRewindAndAdvance(side);
    case 'pawn-frontal-assault': return pawnFrontalAssault(side);
    case 'pawn-omnipresent-passant': return pawnOmnipresentPassant(side);
    case 'pawn-vanguard-promotion': return pawnVanguardPromotion(side);
    case 'pawn-ascendant-royalty': return pawnAscendantRoyalty(side);
    case 'pawn-mini-rooks': return pawnMiniRooks(side);
    case 'knight-double-trot': return knightDoubleTrot(side);
    case 'knight-chill-knight': return knightChillKnight(side);
    case 'knight-tactical-swap': return knightTacticalSwap(side);
    case 'knight-shackling-anchor': return knightShacklingAnchor(side);
    case 'bishop-vaulting-bishop': return bishopVaulting(side);
    case 'bishop-cardinal-nudge': return bishopCardinalNudge(side);
    case 'rook-vaulting-rook': return rookVaulting(side);
    case 'rook-literal-fortress': return rookLiteralFortress(side);
    case 'rook-rook-zone': return rookRookZone(side);
    case 'queen-royal-cavalier': return queenRoyalCavalier(side);
    case 'queen-short-range-burst': return queenShortRangeBurst(side);
    case 'queen-the-matriarchy': return queenTheMatriarchy(side);
    case 'king-telekinetic-strike': return kingTelekineticStrike(side);
    case 'king-phalanx-castle': return kingPhalanxCastle(side);
    case 'general-momentum-check': return generalMomentumCheck(side);
    case 'general-vaulting-army': return generalVaultingArmy(side);
    case 'general-retaliatory-strike': return generalRetaliatoryStrike(side);
    case 'general-knighted-retinue': return generalKnightedRetinue(side);
    case 'general-stored-tempo': return generalStoredTempo(side);
    case 'general-flexible-deployment': return generalFlexibleDeployment(side);
    case 'general-royal-escort': return generalRoyalEscort(side);
    case 'general-castle-master': return generalCastleMaster(side);
    default: return undefined;
  }
}

export { applyStartingSetupForCard };

/**
 * Build the full set of modifiers for a game with `whiteCard` and `blackCard`
 * selected. Either may be null (e.g. casual handicapped games).
 */
export function modifiersForGame(opts: {
  whiteCard: string | null;
  blackCard: string | null;
}): RuleModifier[] {
  const out: RuleModifier[] = [];
  if (opts.whiteCard) {
    const m = modifierForCard(opts.whiteCard, 'white');
    if (m) out.push(m);
  }
  if (opts.blackCard) {
    const m = modifierForCard(opts.blackCard, 'black');
    if (m) out.push(m);
  }
  return out;
}
